import { defineField, defineType } from 'sanity'
// import AutoTagNotes from '../../components/AutoTagNotes' // TODO: Add after component migration

export default defineType({
  name: 'fitmentTag',
  title: 'Fitment Tag',
  type: 'document',
  fields: [
    defineField({
      title: 'Note',
      name: 'note',
      type: 'string',
      // TODO: Add component after migration
      // components: {
      //   input: AutoTagNotes,
      // },
    }),
    defineField({
      name: 'name',
      title: 'Name',
      type: 'string',
      description:
        'Unique identifier for the fitment tag. Used for internal identification.',
      validation: (Rule) => Rule.required(),
    }),
    defineField({
      name: 'displayName',
      title: 'Display Name',
      type: 'string',
      description:
        'Visible on fitment listing. Keep it short and descriptive. No more than 25 characters.',
      validation: (Rule) =>
        Rule.required()
          .max(25)
          .error('Display name must be 25 characters or less'),
    }),
    defineField({
      name: 'filterName',
      title: 'Filter Name',
      type: 'string',
      description:
        'Visible on fitment guide filters dropdown. Keep it short and descriptive. No more than 25 characters.',
      validation: (Rule) =>
        Rule.max(25).error('Filter name must be 25 characters or less'),
    }),
    defineField({
      name: 'fitmentNotes',
      title: 'Fitment Notes - Description',
      type: 'array',
      of: [
        {
          type: 'block',
          styles: [{ title: 'Normal', value: 'normal' }],
          lists: [
            { title: 'Bullet', value: 'bullet' },
            { title: 'Numbered', value: 'number' },
          ],
          marks: {
            decorators: [
              { title: 'Strong', value: 'strong' },
              { title: 'Emphasis', value: 'em' },
            ],
          },
        },
      ],
    }),
    defineField({
      name: 'isWarning',
      title: 'Warning',
      type: 'boolean',
      description: 'Enable to mark this fitment tag as a warning',
      initialValue: false,
    }),
    defineField({
      name: 'isFilterable',
      title: 'Filterable',
      type: 'boolean',
      description: 'Enable to make this tag available in filters',
      initialValue: false,
    }),
    defineField({
      name: 'isModification',
      title: 'isModification',
      type: 'boolean',
      description:
        'When activated, this option will appear under the "Acceptable Modifications" category in the filter menu',
      initialValue: false,
    }),
    defineField({
      name: 'isHidden',
      title: 'Hidden',
      type: 'boolean',
      description: 'Enable to hide the tag label from the UI',
      initialValue: false,
    }),
    defineField({
      name: 'order',
      title: 'Filter Order',
      type: 'number',
      description:
        'Used to control the display order (lower numbers appear first)',
      validation: (Rule) => Rule.integer(),
    }),
    defineField({
      name: 'penaltyScore',
      title: 'Penalty Score',
      type: 'number',
      description:
        'Points deducted from "Ease of fit" score when this tag is applied. Higher penalties indicate more difficult fitments. Maximum penalty is 10 points.',
      validation: (Rule) => Rule.min(0).max(10).precision(1),
      initialValue: 0,
    }),
  ],
  orderings: [
    {
      title: 'Order',
      name: 'orderAsc',
      by: [{ field: 'order', direction: 'asc' }],
    },
    {
      title: 'Name A-Z',
      name: 'nameAsc',
      by: [{ field: 'name', direction: 'asc' }],
    },
    {
      title: 'Filterable First',
      name: 'filterableAsc',
      by: [{ field: 'isFilterable', direction: 'asc' }],
    },
  ],
  preview: {
    select: {
      name: 'name',
      displayName: 'displayName',
      filterable: 'isFilterable',
      fitmentNotes: 'fitmentNotes',
    },
    prepare({ name, displayName, filterable, fitmentNotes }) {
      // Extract text content from fitmentNotes blocks if it exists
      let notesPreview = ''
      if (fitmentNotes && fitmentNotes.length > 0) {
        // Try to extract text from the first block
        const firstBlock = fitmentNotes[0]
        if (firstBlock.children && firstBlock.children.length > 0) {
          // Collect text from children
          notesPreview = firstBlock.children
            .filter((child: any) => child.text)
            .map((child: any) => child.text)
            .join(' ')
            .substring(0, 50) // Limit to 50 chars

          if (notesPreview.length === 50) {
            notesPreview += '...'
          }
        }
      }

      return {
        title: name,
        subtitle:
          notesPreview || displayName || (filterable ? 'Filterable' : ''),
      }
    },
  },
})
